// External packages
import * as React from 'react';

// Utils
import { twMerge } from 'tailwind-merge';
import { getBestImageUrl } from 'utils/getImageUrl';

// Components
import { Icon } from 'components/Icons';

// Types
import { Image } from '../../../../server/src/types';

export const ExhibitGallery: React.FC<{
  images: Image[];
  altBase: string;
}> = ({ images, altBase }) => {
  const [currentImageIdx, setCurrentImageIdx] = React.useState(0);
  const totalImages = images.length;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIdx((idx) => (idx > 0 ? idx - 1 : idx));
  };
  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIdx((idx) => (idx < totalImages - 1 ? idx + 1 : idx));
  };

  React.useEffect(() => {
    setCurrentImageIdx(0);
  }, [images]);

  if (!images || images.length === 0) {
    return (
      <div className="w-full bg-gray-50 relative flex items-center justify-center h-48 min-h-48 rounded text-gray-500">
        No images available.
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 relative select-none">
      {totalImages > 1 && (
        <button
          className="absolute cursor-pointer left-2 top-1/2 -translate-y-1/2 bg-white rounded-full shadow w-6 h-6 flex items-center justify-center disabled:opacity-30 z-10"
          onClick={handlePrev}
          disabled={currentImageIdx === 0}
          aria-label="Previous image"
        >
          <Icon name="chevron-down" className="rotate-90" />
        </button>
      )}
      {totalImages > 1 && (
        <button
          className="absolute cursor-pointer right-2 top-1/2 -translate-y-1/2 bg-white rounded-full shadow w-6 h-6 flex items-center justify-center disabled:opacity-30 z-10"
          onClick={handleNext}
          disabled={currentImageIdx === totalImages - 1}
          aria-label="Next image"
        >
          <Icon name="chevron-down" className="-rotate-90" />
        </button>
      )}
      <div className="relative h-48 min-w-32 min-h-48 touch-pan-y w-full overflow-hidden flex items-center justify-center mx-auto">
        <div
          className={twMerge(
            'flex transition-transform duration-500 ease-in-out h-48 w-full'
          )}
          style={{
            transform: `translateX(-${currentImageIdx * 100}%)`,
          }}
        >
          {images.map((img, idx) => (
            <img
              key={img.id || idx}
              src={getBestImageUrl(img, 'medium')}
              alt={altBase + ' image ' + (idx + 1)}
              className="w-full h-full min-w-full min-h-48 object-cover flex-shrink-0"
              draggable={false}
            />
          ))}
        </div>
      </div>
      {totalImages > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
          {images.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={twMerge(
                'h-2 w-2 rounded-full cursor-pointer focus:outline-none transition-colors duration-200',
                idx === currentImageIdx ? 'bg-blue-500' : 'bg-gray-300'
              )}
              onClick={() => setCurrentImageIdx(idx)}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
